import { useState, useEffect } from 'react';

/**
 * Custom hook to get an API client for making GraphQL requests
 * This hook will handle the initialization of the API client and manage its state.
 * It assumes that Amplify is already configured at the application startup.
 */
export const useApiClient = () => {
  const [client, setClient] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initializeClient = async () => {
      try {
        // Import the API client dynamically to avoid circular dependencies
        const { generateClient } = await import('aws-amplify/api');
        
        // Create the client
        const apiClient = generateClient();
        
        // Update state if component is still mounted
        if (isMounted) {
          setClient(apiClient);
          setIsLoading(false);
          console.log('API client initialized successfully');
        }
      } catch (err: any) {
        console.error('Error initializing API client:', err);
        if (isMounted) {
          setError(err);
          setIsLoading(false);
        }
      }
    };

    initializeClient();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Get a new API client
   * This can be used if the client needs to be re-initialized or if it wasn't initialized properly
   */
  const getClient = async () => {
    try {
      setIsLoading(true);
      
      // Import the API client dynamically to avoid circular dependencies
      const { generateClient } = await import('aws-amplify/api');
      
      // Create the client
      const apiClient = generateClient();
      
      setClient(apiClient);
      setIsLoading(false);
      setError(null);
      
      return apiClient;
    } catch (err: any) {
      console.error('Error getting API client:', err);
      setError(err);
      setIsLoading(false);
      throw err;
    }
  };

  return {
    client,
    isLoading,
    error,
    getClient
  };
};
